import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

import { colorForUser } from './userColor.js';
import { type ShiftSummaryItem, useShiftsSummary } from '../../api/shifts.api.js';
import { useUsers } from '../../api/users.api.js';
import type { Monitor } from '../../models/index.js';
import { formatHoursMinutes } from '../../utils/formatHoursMinutes.js';

interface SummaryRow extends Monitor {
  totalMinutes: number;
}

interface HoursSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  // The roster calendar's currently visible range (FullCalendar startStr/endStr;
  // end is exclusive). Null until the calendar emits its first datesSet.
  range: { start: string; end: string } | null;
}

function buildRows(monitors: Monitor[], data: ShiftSummaryItem[] | undefined): SummaryRow[] {
  const summaryMap = new Map((data ?? []).map((s) => [s.userId, s.totalMinutes]));
  return [...monitors]
    .map((m) => ({ ...m, totalMinutes: summaryMap.get(m.id) ?? 0 }))
    .sort((a, b) => b.totalMinutes - a.totalMinutes || a.name.localeCompare(b.name));
}

function formatRangeLabel(range: { start: string; end: string } | null): string {
  if (!range) {
    return '';
  }
  const start = DateTime.fromISO(range.start);
  // FullCalendar's range end is exclusive (midnight after the last visible day).
  const lastDay = DateTime.fromISO(range.end).minus({ days: 1 });
  if (!start.isValid || !lastDay.isValid) {
    return '';
  }
  if (start.hasSame(lastDay, 'day')) {
    return start.toFormat('LLL d, yyyy');
  }
  return `${start.toFormat('LLL d')} – ${lastDay.toFormat('LLL d, yyyy')}`;
}

interface SummaryRowItemProps {
  row: SummaryRow;
}

function SummaryRowItem({ row }: SummaryRowItemProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ py: 1, borderRadius: 1, px: 1, '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Avatar
        src={row.picture}
        alt={row.name}
        sx={{ width: 28, height: 28, fontSize: 12, border: `2px solid ${colorForUser(row.id)}`, flexShrink: 0 }}
      >
        {row.name.charAt(0)}
      </Avatar>
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {row.name}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: row.totalMinutes === 0 ? 'text.secondary' : 'text.primary', whiteSpace: 'nowrap' }}
      >
        {formatHoursMinutes(row.totalMinutes)}
      </Typography>
    </Stack>
  );
}

const HoursSummaryDialog = ({ open, onClose, range }: HoursSummaryDialogProps) => {
  const { data: monitors = [] } = useUsers();
  // Only fetch while the dialog is open — avoids a summary request on every
  // calendar range change when the dialog is closed.
  const { data, isInitialLoading, isError } = useShiftsSummary(
    open ? range?.start : undefined,
    open ? range?.end : undefined,
  );

  const rows = useMemo(() => buildRows(monitors, data), [monitors, data]);
  const grandTotal = rows.reduce((sum, r) => sum + r.totalMinutes, 0);
  const rangeLabel = formatRangeLabel(range);
  const showSkeleton = range === null || isInitialLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        Hours Summary
        {rangeLabel && (
          <Typography variant="caption" color="text.secondary">{rangeLabel}</Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isError && (
          <Alert severity="error">Could not load hours. Try a different date range or refresh the page.</Alert>
        )}

        {showSkeleton && (
          <Box>
            <Skeleton height={36} sx={{ mb: 0.5 }} />
            <Skeleton height={36} sx={{ mb: 0.5 }} />
            <Skeleton height={36} sx={{ mb: 0.5 }} />
            <Skeleton height={36} sx={{ mb: 0.5 }} />
          </Box>
        )}

        {!showSkeleton && monitors.length === 0 && (
          <Typography variant="body2" color="text.secondary">No monitors found.</Typography>
        )}

        {!showSkeleton && monitors.length > 0 && (
          <>
            <Stack spacing={0}>
              {rows.map((row) => (
                <SummaryRowItem key={row.id} row={row} />
              ))}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" sx={{ px: 1 }}>
              <Typography variant="body2" color="text.secondary">Total</Typography>
              <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 600 }}>
                {formatHoursMinutes(grandTotal)}
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HoursSummaryDialog;
